import { Getter, Setter, WritableAtom } from 'jotai';
import { MqttClient } from 'mqtt';
import { mqttLogger } from '@hooks/mqtt/utils';
import { PendingUnsubscribe, SubscriptionConfig } from '../types';
import {
  handleSubscriptionError,
  logSubscriptionEvent,
  SubscriptionEvent,
} from './atomUtils';
import { unsubscribeFromTopicAtom } from '../subscription/unsubscribeFromTopicAtom';
import { manageListenerLifecycleAtom } from '../message/mqttListenerAtom';
import {
  mqttClientAtom,
  mqttConnectionAtom,
} from '../connection/mqttConnectionAtom';
import { MqttConnectionActionType } from '../connection/types';

export const UNSUBSCRIBE_DELAY_MS = 200;

export function clearExistingTimeout(
  key: string,
  timeouts: Map<string, NodeJS.Timeout>,
): Map<string, NodeJS.Timeout> {
  const existingTimeout = timeouts.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
    const newTimeouts = new Map(timeouts);
    newTimeouts.delete(key);
    return newTimeouts;
  }
  return timeouts;
}

export function addPendingUnsubscribe(
  key: string,
  topic: string,
  subscriberId: string,
  pendingMap: Map<string, PendingUnsubscribe>,
): Map<string, PendingUnsubscribe> {
  const newPending = new Map(pendingMap);
  newPending.set(key, {
    topic,
    subscriberId,
    timestamp: Date.now(),
  });
  return newPending;
}

export function removePendingUnsubscribe(
  key: string,
  pendingMap: Map<string, PendingUnsubscribe>,
): Map<string, PendingUnsubscribe> {
  const newPending = new Map(pendingMap);
  newPending.delete(key);
  return newPending;
}

export function clearAllTimeouts(timeouts: Map<string, NodeJS.Timeout>): void {
  timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
}

export async function executeForceUnsubscribe(
  set: Setter,
  topic: string,
  subscriberId: string,
): Promise<void> {
  mqttLogger.debug(
    `Force unsubscribing from ${topic} (subscriber: ${subscriberId})`,
  );
  await set(unsubscribeFromTopicAtom, { topic, subscriberId });
  set(manageListenerLifecycleAtom, 'decrement');
}

export async function executeDebouncedUnsubscribe(
  get: Getter,
  set: Setter,
  key: string,
  topic: string,
  subscriberId: string,
  pendingUnsubscribesAtom: WritableAtom<
    Map<string, PendingUnsubscribe>,
    [Map<string, PendingUnsubscribe>],
    void
  >,
  pendingTimeoutsAtom: WritableAtom<
    Map<string, NodeJS.Timeout>,
    [Map<string, NodeJS.Timeout>],
    void
  >,
): Promise<void> {
  const stillPending = get(pendingUnsubscribesAtom);
  if (!stillPending.has(key)) {
    return;
  }

  mqttLogger.debug(
    `Executing debounced unsubscribe for ${topic} (subscriber: ${subscriberId})`,
  );

  await set(unsubscribeFromTopicAtom, { topic, subscriberId });
  set(manageListenerLifecycleAtom, 'decrement');

  const updatedPending = removePendingUnsubscribe(key, stillPending);
  set(pendingUnsubscribesAtom, updatedPending);

  const timeouts = get(pendingTimeoutsAtom);
  const updatedTimeouts = clearExistingTimeout(key, timeouts);
  set(pendingTimeoutsAtom, updatedTimeouts);
}

export function scheduleUnsubscribeTimeout(
  callback: () => Promise<void>,
  delay: number,
): NodeJS.Timeout {
  return setTimeout(callback, delay);
}

export function logScheduledUnsubscribe(
  topic: string,
  subscriberId: string,
  delay: number,
): void {
  mqttLogger.debug(
    `Scheduled unsubscribe for ${topic} (subscriber: ${subscriberId}) with ${delay}ms delay`,
  );
}

export function logCancelledUnsubscribe(
  topic: string,
  subscriberId: string,
): void {
  mqttLogger.debug(
    `Cancelled pending unsubscribe for ${topic} (subscriber: ${subscriberId})`,
  );
}

export function logFlushingUnsubscribes(count: number): void {
  mqttLogger.info(`Flushing ${count} pending unsubscriptions`);
}

export async function flushAllPendingUnsubscribes(
  set: Setter,
  pendingUnsubscribes: Map<string, PendingUnsubscribe>,
): Promise<void> {
  const unsubscribePromises = Array.from(pendingUnsubscribes.values()).map(
    (pending) =>
      set(unsubscribeFromTopicAtom, {
        topic: pending.topic,
        subscriberId: pending.subscriberId,
      }),
  );

  await Promise.all(unsubscribePromises);
}

export const createTopicsKey = (topics: string[]): string => {
  return topics.slice().sort().join('|');
};

export const areTopicsEqual = (
  topicsA: string[],
  topicsB: string[],
): boolean => {
  return createTopicsKey(topicsA) === createTopicsKey(topicsB);
};

export const createMultiTopicDebugLabel = (
  topics: string[],
  maxTopicsToShow = 5,
): string => {
  const visibleTopics = topics.slice(0, maxTopicsToShow).join(',');
  const suffix = topics.length > maxTopicsToShow ? '...' : '';
  return `multiTopic:[${visibleTopics}${suffix}]`;
};

export function validateTopic(topic: string, subscriberId: string): boolean {
  if (!topic || topic.trim() === '') {
    mqttLogger.error(
      `Invalid topic provided for subscription: ${JSON.stringify(topic)} (subscriber: ${subscriberId})`,
    );
    return false;
  }
  return true;
}

export async function ensureClientConnected(
  get: Getter,
  set: Setter,
  topic: string,
): Promise<MqttClient | null> {
  let client: MqttClient | null = get(mqttClientAtom);

  if (client && client.connected) {
    return client;
  }

  try {
    client = (await set(mqttConnectionAtom, {
      type: MqttConnectionActionType.ENSURE,
    })) as MqttClient | null;

    if (!client) {
      logSubscriptionEvent(SubscriptionEvent.Error, topic, {
        message: `Cannot subscribe to ${topic}: MQTT client initialization failed`,
      });
      return null;
    }
  } catch (error) {
    logSubscriptionEvent(SubscriptionEvent.Error, topic, {
      message: `Cannot subscribe to ${topic}: ${error instanceof Error ? error.message : 'MQTT client initialization failed'}`,
    });
    return null;
  }

  if (!client.connected) {
    logSubscriptionEvent(SubscriptionEvent.Error, topic, {
      message: `Cannot subscribe to ${topic}: MQTT client not connected to broker`,
    });
    return null;
  }

  return client;
}

export async function performMqttSubscription(
  client: MqttClient,
  topic: string,
  config?: SubscriptionConfig,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    client.subscribe(topic, { qos: config?.qos ?? 0 }, (err: Error | null) => {
      if (err) {
        mqttLogger.error(`MQTT subscribe error for ${topic}:`, err);
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

export function handleSubscriptionFailure(topic: string, error: unknown): void {
  handleSubscriptionError(topic, error, 'Failed to subscribe');
}

export function cleanupAtomFamilies(
  topic: string,
  ...atomFamilies: Array<{ remove: (param: string) => void }>
): void {
  atomFamilies.forEach((atomFamily) => {
    atomFamily.remove(topic);
  });
}

export async function unsubscribeFromMqttTopic(
  client: MqttClient,
  topic: string,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    client.unsubscribe(topic, (err) => {
      if (err) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

export async function handleLastSubscriberUnsubscribe(
  client: MqttClient | null,
  topic: string,
): Promise<void> {
  if (!client) {
    return;
  }

  try {
    await unsubscribeFromMqttTopic(client, topic);
    logSubscriptionEvent(SubscriptionEvent.Unsubscribe, topic);
  } catch (error) {
    mqttLogger.error(`Failed to unsubscribe from topic ${topic}:`, error);
  }
}

export function logSubscriberRemoval(
  topic: string,
  subscriberId: string,
  remainingSubscriberCount: number,
): void {
  logSubscriptionEvent(SubscriptionEvent.RemoveSubscriber, topic, {
    subscriberId,
    subscriberCount: remainingSubscriberCount,
  });
}

export function isValidSubscriber(
  subscribersSet: Set<string>,
  subscriberId: string,
): boolean {
  return subscribersSet.has(subscriberId);
}

export function isLastSubscriber(subscribersSet: Set<string>): boolean {
  return subscribersSet.size === 1;
}

export function validateUnsubscriptionTopic(
  topic: string,
  subscriberId: string,
): boolean {
  if (!topic || topic.trim() === '') {
    mqttLogger.error(
      `Invalid topic provided for unsubscription: ${JSON.stringify(topic)} (subscriber: ${subscriberId})`,
    );
    return true;
  }
  return false;
}

export function checkSubscriberExists(
  currentSubscribers: Set<string>,
  subscriberId: string,
  topic: string,
): boolean {
  if (!currentSubscribers.has(subscriberId)) {
    mqttLogger.debug(`Subscriber ${subscriberId} not found for topic ${topic}`);
    return true;
  }
  return false;
}

export function scheduleUnsubscribe(
  set: Setter,
  scheduleDebouncedUnsubscribeAtom: any,
  topic: string,
  subscriberId: string,
  immediate: boolean,
): void {
  if (immediate) {
    mqttLogger.debug(
      `Immediate unsubscribe requested for ${topic} (subscriber: ${subscriberId})`,
    );
    set(scheduleDebouncedUnsubscribeAtom, {
      topic,
      subscriberId,
      force: true,
    });
  } else {
    set(scheduleDebouncedUnsubscribeAtom, {
      topic,
      subscriberId,
    });
  }
}
